import React, { Component } from "react";
import { Input, Modal } from "reactstrap";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import {
  getActivityLogs,
  getActivityLogById,
  getActivityLogFilters,
} from "../../Endpoint/Admin";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import PeriodFilter, { allTime, periodToParams } from "../../components/Common/PeriodFilter";
import {
  C, Panel, MiniStat, StatRow, SearchPill, Table, Chip, Btn, Avatar, Field, IconBtn,
} from "../../components/Console/ConsoleUI";

/**
 * Activity log — the console's audit trail.
 *
 * Read-only by nature: every control here narrows what is shown, none of them
 * change anything, so there is nothing on the page to gate. The filters sit in
 * their own panel above a quiet table, and a row opens the full record
 * (including the before/after diff) in a dialog.
 */

/** Console-surface selects and date fields. */
const CSS = `
.act-form .form-control,.act-form .custom-select{height:42px;padding:0 13px;background:#fff;
  border:1px solid ${C.border};border-radius:12px;font-size:14px;color:${C.ink};box-shadow:none}
.act-form .form-control:focus{border-color:${C.sand};box-shadow:none}
.act-form select.form-control{padding-right:26px}
.act-json{margin:0;padding:12px 14px;border-radius:12px;background:${C.surfaceAlt};
  color:${C.brownDeep};font-size:12px;max-height:230px;overflow:auto}
`;

/** Actions read as consequences, so destructive ones are coloured as such. */
const ACTION_TONE = {
  CREATE: "good",
  UPDATE: "neutral",
  DELETE: "bad",
  STATUS_CHANGE: "warn",
  TOGGLE: "warn",
  LOGIN: "neutral",
  LOGOUT: "neutral",
  ASSIGN: "neutral",
  EXPORT: "neutral",
};

/** Numbered pager, matching the console's 34px rounded squares. */
const Pager = ({ page, pages, onGo }) => {
  if (pages <= 1) return null;
  const box = (extra) => ({
    width: 34, height: 34, borderRadius: 10, display: "grid", placeItems: "center",
    fontSize: 14, cursor: "pointer", border: `1px solid ${C.border}`,
    background: "#fff", color: C.slate, ...extra,
  });
  const numbers = Array.from({ length: Math.min(5, pages) }, (_, i) => {
    if (pages <= 5) return i + 1;
    if (page <= 3) return i + 1;
    if (page >= pages - 2) return pages - 4 + i;
    return page - 2 + i;
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span
        onClick={() => page > 1 && onGo(page - 1)}
        style={box({ color: page > 1 ? C.slate : "#bfae99", cursor: page > 1 ? "pointer" : "default" })}
      >
        <span className="ms" style={{ fontSize: 19 }}>chevron_left</span>
      </span>
      {numbers.map((n) => (
        <span
          key={n}
          onClick={() => onGo(n)}
          style={box(n === page ? { background: C.brown, color: "#fff", border: "none" } : {})}
        >
          {n}
        </span>
      ))}
      <span
        onClick={() => page < pages && onGo(page + 1)}
        style={box({ color: page < pages ? C.slate : "#bfae99", cursor: page < pages ? "pointer" : "default" })}
      >
        <span className="ms" style={{ fontSize: 19 }}>chevron_right</span>
      </span>
    </div>
  );
};

class ActivityLogList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      logs: [],
      pagination: { page: 1, limit: 20, total: 0, pages: 1 },
      loading: true,
      filterOptions: { modules: [], actions: [], users: [] },
      filters: {
        userId: "",
        module: "",
        action: "",
        search: "",
      },
      period: allTime(),
      selectedLog: null,
      detailModal: false,
    };
  }

  componentDidMount() {
    this.loadFilters();
    this.loadLogs();
  }

  loadFilters = async () => {
    const response = await getActivityLogFilters();
    if (response && response.success) {
      this.setState({ filterOptions: response.data });
    }
  };

  loadLogs = async (page = 1) => {
    this.setState({ loading: true });
    const { filters, period, pagination } = this.state;

    const params = {
      page,
      limit: pagination.limit,
      ...Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "")
      ),
      ...periodToParams(period),
    };

    const response = await getActivityLogs(params);
    if (response && response.success) {
      this.setState({
        logs: response.data,
        pagination: response.pagination,
        loading: false,
      });
    } else {
      this.setState({ loading: false });
    }
  };

  handleFilterChange = (e) => {
    const { name, value } = e.target;
    this.setState(
      (prevState) => ({
        filters: { ...prevState.filters, [name]: value },
      }),
      () => {
        if (name !== "search") {
          this.loadLogs(1);
        }
      }
    );
  };

  handlePeriodChange = (period) => {
    this.setState({ period }, () => this.loadLogs(1));
  };

  /** The search pill is not a <form>, so Enter is wired explicitly. */
  handleSearchChange = (value) => {
    this.setState((prev) => ({ filters: { ...prev.filters, search: value } }));
  };

  handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    this.loadLogs(1);
  };

  clearFilters = () => {
    this.setState(
      {
        filters: {
          userId: "",
          module: "",
          action: "",
          search: "",
        },
        period: allTime(),
      },
      () => this.loadLogs(1)
    );
  };

  viewLogDetail = async (log) => {
    const response = await getActivityLogById(log._id);
    if (response && response.success) {
      this.setState({ selectedLog: response.data, detailModal: true });
    }
  };

  toggleDetailModal = () => {
    this.setState((prevState) => ({
      detailModal: !prevState.detailModal,
      selectedLog: prevState.detailModal ? null : prevState.selectedLog,
    }));
  };

  formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  actionChip = (action) => (
    <Chip tone={ACTION_TONE[action] || "neutral"}>{action}</Chip>
  );

  moduleChip = (module) => <Chip tone="neutral">{module}</Chip>;

  hasFilters() {
    return this.state.period.mode !== "all" || Object.values(this.state.filters).some((v) => v !== "");
  }

  columns() {
    return [
      {
        key: "createdAt",
        label: "When",
        width: 170,
        render: (log) => (
          <span style={{ color: C.taupe }}>{this.formatDate(log.createdAt)}</span>
        ),
      },
      {
        key: "user",
        label: "Who",
        render: (log) => (
          <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <Avatar name={log.userName} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, color: C.ink }}>{log.userName || "Unknown"}</div>
              <div style={{ fontSize: 12, color: C.taupeLight }}>{log.userEmail || "—"}</div>
            </div>
          </div>
        ),
      },
      {
        key: "module",
        label: "Module",
        width: 130,
        render: (log) => this.moduleChip(log.module),
      },
      {
        key: "action",
        label: "Action",
        width: 140,
        render: (log) => this.actionChip(log.action),
      },
      {
        key: "description",
        label: "What happened",
        render: (log) => (
          <span
            title={log.description}
            style={{
              display: "inline-block", maxWidth: 360, overflow: "hidden",
              textOverflow: "ellipsis", whiteSpace: "nowrap", color: C.slate,
            }}
          >
            {log.description}
          </span>
        ),
      },
      {
        key: "view",
        label: "",
        width: 60,
        align: "right",
        render: (log) => (
          <div onClick={(e) => e.stopPropagation()}>
            <IconBtn icon="visibility" title="Open this entry" onClick={() => this.viewLogDetail(log)} />
          </div>
        ),
      },
    ];
  }

  renderFilters() {
    const { filters, filterOptions, period } = this.state;
    return (
      <Panel
        title="Filters"
        subtitle="Every control here narrows the list — nothing on this page changes a record"
        actions={
          this.hasFilters() ? (
            <Btn kind="quiet" icon="filter_alt_off" onClick={this.clearFilters}>
              Clear filters
            </Btn>
          ) : null
        }
      >
        <div
          className="act-form"
          style={{
            marginTop: 18,
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 14,
          }}
        >
          <Field label="Module">
            <Input type="select" name="module" value={filters.module} onChange={this.handleFilterChange}>
              <option value="">All modules</option>
              {(filterOptions.modules || []).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </Input>
          </Field>
          <Field label="Action">
            <Input type="select" name="action" value={filters.action} onChange={this.handleFilterChange}>
              <option value="">All actions</option>
              {(filterOptions.actions || []).map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </Input>
          </Field>
          <Field label="User">
            <Input type="select" name="userId" value={filters.userId} onChange={this.handleFilterChange}>
              <option value="">Everyone</option>
              {(filterOptions.users || []).map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </Input>
          </Field>
          <Field label="Period" style={{ gridColumn: "1 / -1" }}>
            <PeriodFilter value={period} onChange={this.handlePeriodChange} />
          </Field>
        </div>
      </Panel>
    );
  }

  renderChanges(changes) {
    if (!changes || (!changes.before && !changes.after)) return null;
    const block = (title, value) => (
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, letterSpacing: ".6px", textTransform: "uppercase", color: C.taupe, marginBottom: 7 }}>
          {title}
        </div>
        <pre className="act-json">{JSON.stringify(value, null, 2)}</pre>
      </div>
    );
    return (
      <div style={{ marginTop: 18 }}>
        <div style={{ fontSize: 15, color: C.ink, marginBottom: 10 }}>Changes</div>
        <div style={{ display: "flex", gap: 12 }}>
          {changes.before && block("Before", changes.before)}
          {changes.after && block("After", changes.after)}
        </div>
      </div>
    );
  }

  renderDetailModal() {
    const { selectedLog, detailModal } = this.state;
    const line = (label, value) => (
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 14, color: C.slate, marginTop: 9 }}>
        <span>{label}</span>
        <span style={{ color: C.ink, textAlign: "right" }}>{value || "N/A"}</span>
      </div>
    );

    return (
      <Modal
        isOpen={detailModal}
        toggle={this.toggleDetailModal}
        size="lg"
        centered
        contentClassName="bg-transparent border-0"
          className="con-modal"
        >
        {selectedLog && (
          <div style={{ background: "#fdf9f3", borderRadius: 22, padding: 30 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
              <span
                className="ms"
                style={{ fontSize: 24, color: C.brown, background: C.iconTint, padding: 12, borderRadius: 15 }}
              >
                history
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 20, fontWeight: 500, color: C.ink }}>
                  {selectedLog.description || "Activity entry"}
                </div>
                <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {this.moduleChip(selectedLog.module)}
                  {this.actionChip(selectedLog.action)}
                  <span style={{ fontSize: 13, color: C.taupe }}>
                    {this.formatDate(selectedLog.createdAt)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={this.toggleDetailModal}
                aria-label="Close"
                style={{ border: 0, background: "transparent", color: C.taupeLight, cursor: "pointer", padding: 4, lineHeight: 1 }}
              >
                <span className="ms" style={{ fontSize: 22 }}>close</span>
              </button>
            </div>

            <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ padding: "16px 18px", borderRadius: 14, background: C.iconTint }}>
                <div style={{ fontSize: 12, letterSpacing: ".6px", color: C.taupe }}>WHO</div>
                {line("Name", selectedLog.userName)}
                {line("Email", selectedLog.userEmail)}
                {line("IP address", selectedLog.ipAddress)}
              </div>
              <div style={{ padding: "16px 18px", borderRadius: 14, background: C.iconTint }}>
                <div style={{ fontSize: 12, letterSpacing: ".6px", color: C.taupe }}>WHAT</div>
                {line("Entity type", selectedLog.entityType)}
                {line("Entity name", selectedLog.entityName)}
                {line("When", this.formatDate(selectedLog.createdAt))}
              </div>
            </div>

            {this.renderChanges(selectedLog.changes)}

            {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 15, color: C.ink, marginBottom: 10 }}>Metadata</div>
                <pre className="act-json">{JSON.stringify(selectedLog.metadata, null, 2)}</pre>
              </div>
            )}

            <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
              <Btn kind="quiet" onClick={this.toggleDetailModal}>Close</Btn>
            </div>
          </div>
        )}
      </Modal>
    );
  }

  render() {
    const { logs, loading, filters, pagination } = this.state;
    const people = new Set(logs.map((l) => l.userEmail || l.userName).filter(Boolean)).size;
    const modules = new Set(logs.map((l) => l.module).filter(Boolean)).size;
    const destructive = logs.filter((l) => l.action === "DELETE").length;

    return (
      <ConsolePage
        title="Activity logs"
        subtitle={
          loading
            ? "Loading the audit trail…"
            : `${pagination.total} entr${pagination.total === 1 ? "y" : "ies"} recorded`
        }
        actions={
          <React.Fragment>
            <form onSubmit={this.handleSearchSubmit} style={{ margin: 0 }}>
              <SearchPill
                value={filters.search}
                onChange={this.handleSearchChange}
                placeholder="Search descriptions"
                width={250}
              />
            </form>
            <Btn icon="search" onClick={() => this.handleSearchSubmit()}>Search</Btn>
          </React.Fragment>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <StatRow>
            <MiniStat label="Entries recorded" value={pagination.total || 0} hint="Matching the current filters" />
            <MiniStat label="On this page" value={logs.length} hint={`Page ${pagination.page || 1} of ${pagination.pages || 1}`} />
            <MiniStat label="People active" value={people} hint="Distinct accounts on this page" />
            <MiniStat
              label="Deletions"
              value={destructive}
              tone={destructive > 0 ? "bad" : undefined}
              hint={`${modules} module${modules === 1 ? "" : "s"} touched on this page`}
            />
          </StatRow>

          {this.renderFilters()}

          <Panel style={{ padding: 0 }}>
            <div
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, padding: "16px 22px", borderBottom: `1px solid ${C.panel}`,
              }}
            >
              <div style={{ fontSize: 17, fontWeight: 500, color: C.ink }}>What happened</div>
              <div style={{ fontSize: 14, color: C.taupeLight }}>
                {this.hasFilters() ? "Filtered view" : "Everything, newest first"}
              </div>
            </div>

            <div style={{ padding: "0 10px" }}>
              {loading ? (
                <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
                  Loading activity logs…
                </div>
              ) : (
                <Table
                  columns={this.columns()}
                  rows={logs}
                  empty={
                    this.hasFilters()
                      ? "No activity matches these filters"
                      : "Nothing has been logged yet"
                  }
                  onRowClick={this.viewLogDetail}
                />
              )}
            </div>

            <div
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "14px 22px", borderTop: `1px solid ${C.panel}`,
              }}
            >
              <div style={{ fontSize: 14, color: C.taupeLight }}>
                Page {pagination.page || 1} of {pagination.pages || 1} · {pagination.total || 0} entr
                {pagination.total === 1 ? "y" : "ies"}
              </div>
              <Pager
                page={pagination.page || 1}
                pages={pagination.pages || 1}
                onGo={(p) => this.loadLogs(p)}
              />
            </div>
          </Panel>
        </div>

        {this.renderDetailModal()}
      </ConsolePage>
    );
  }
}

export default withRouter(withTranslation()(ActivityLogList));
